// ============================================================
// GAME LOGIC — BFS, grid helpers, item placement
// ============================================================
import { GRID_SIZE, ITEM_COUNTS } from './constants';

/** Create a blank 16x16 grid (all nulls) */
export function initGrid() {
  return Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
}

/**
 * BFS from (startRow, startCol) to find the cell with `targetEmoji`
 * Returns { r, c, dist } or null if not found.
 */
export function bfsFind(grid, startRow, startCol, targetEmoji) {
  const visited = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(false));
  const queue = [[startRow, startCol, 0]];
  visited[startRow][startCol] = true;
  const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];

  while (queue.length) {
    const [r, c, dist] = queue.shift();
    if (grid[r][c] === targetEmoji) return { r, c, dist };
    for (const [dr, dc] of dirs) {
      const nr = (r + dr + GRID_SIZE) % GRID_SIZE;
      const nc = (c + dc + GRID_SIZE) % GRID_SIZE;
      if (!visited[nr][nc]) {
        visited[nr][nc] = true;
        queue.push([nr, nc, dist + 1]);
      }
    }
  }
  return null;
}

/**
 * BFS to find a cell that is:
 *  - empty (null)
 *  - at least `minDist` steps from the player
 * Returns the FARTHEST such cell found, so items are never right next to the player.
 */
export function findFarEmptyCell(grid, playerR, playerC, minDist = 2) {
  const visited = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(false));
  const queue = [[playerR, playerC, 0]];
  visited[playerR][playerC] = true;
  const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  let best = null;
  let bestDist = -1;

  while (queue.length) {
    const [r, c, dist] = queue.shift();
    if (dist >= minDist && !grid[r][c] && dist > bestDist) {
      bestDist = dist;
      best = { r, c };
    }
    for (const [dr, dc] of dirs) {
      const nr = (r + dr + GRID_SIZE) % GRID_SIZE;
      const nc = (c + dc + GRID_SIZE) % GRID_SIZE;
      if (!visited[nr][nc]) {
        visited[nr][nc] = true;
        queue.push([nr, nc, dist + 1]);
      }
    }
  }

  // Fallback: random empty cell
  if (!best) {
    const empties = [];
    for (let r = 0; r < GRID_SIZE; r++)
      for (let c = 0; c < GRID_SIZE; c++)
        if (!grid[r][c] && !(r === playerR && c === playerC))
          empties.push({ r, c });
    if (empties.length) return empties[Math.floor(Math.random() * empties.length)];
  }
  return best;
}

/**
 * Count how many of each emoji currently exist on the grid.
 */
export function countItems(grid) {
  const counts = {};
  for (let r = 0; r < GRID_SIZE; r++)
    for (let c = 0; c < GRID_SIZE; c++)
      if (grid[r][c]) counts[grid[r][c]] = (counts[grid[r][c]] || 0) + 1;
  return counts;
}

/**
 * Top-up the grid so there are always the right number of each item.
 * Uses BFS placement so items never appear right next to the player.
 */
export function replenishItems(grid, playerR, playerC) {
  const g = grid.map(row => [...row]);
  const current = countItems(g);

  for (const [emoji, target] of Object.entries(ITEM_COUNTS)) {
    const have = current[emoji] || 0;
    for (let i = have; i < target; i++) {
      const cell = findFarEmptyCell(g, playerR, playerC, 3);
      if (cell) g[cell.r][cell.c] = emoji;
    }
  }
  return g;
}

/** Full fresh start: blank grid + items placed */
export function buildStartGrid(playerR, playerC) {
  const g = initGrid();
  return replenishItems(g, playerR, playerC);
}